import React, { useEffect, useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  LinearProgress,
  Paper,
  Avatar,
} from '@mui/material';
import {
  People as PeopleIcon,
  Storage as StorageIcon,
  FileCopy as FileIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities } from '../store/slices/entitySlice';
import { fetchUsers } from '../store/slices/userSlice';
import { fetchSystemStats, fetchSystemHealth } from '../store/slices/systemSlice';
import { getProcessingStatus, getProcessingHistory } from '../store/slices/dataProcessingSlice';
import { showInfo } from '../store/slices/toastSlice';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { entities, isLoading: entitiesLoading } = useAppSelector((state) => state.entities);
  const { users, isLoading: usersLoading } = useAppSelector((state) => state.users);
  const { stats, health, isLoading: statsLoading } = useAppSelector((state) => state.system);
  const { isProcessing, status, history } = useAppSelector((state) => state.dataProcessing);
  
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Set page meta before any conditional returns to keep hooks stable
  usePageMeta('Dashboard – AskNow', 'Overview of users, entities, system health, and processing in AskNow.');

  useEffect(() => {
    dispatch(fetchEntities());
    dispatch(fetchSystemStats());
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        dispatch(fetchEntities());
        dispatch(fetchSystemStats());
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, dispatch]);

  const handleRefresh = () => {
    dispatch(fetchSystemStats());
    dispatch(fetchSystemHealth());
    dispatch(fetchUsers());
    dispatch(fetchEntities());
    dispatch(getProcessingStatus());
    dispatch(getProcessingHistory());
    dispatch(showInfo({
      title: 'Refreshing',
      message: 'Dashboard data is being refreshed...',
      duration: 2000,
    }));
  };

  const handleProcessData = async () => {
    navigate('/admin/data-processing');
  };

  const COLORS = ['#FF7316', '#1ce9e0', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

  const roleDistributionData = users
    ? Object.entries(
        users.reduce((acc, user) => {
          acc[user.role] = (acc[user.role] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      ).map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1),
        value: count,
      }))
    : [];

  const systemHealthData = health
    ? [
        { service: 'System', status: health.status === 'healthy' ? 'Healthy' : 'Warning' },
        { service: 'Memory', status: health.memory_usage < '80%' ? 'Healthy' : 'Warning' },
        { service: 'CPU', status: health.cpu_usage < '80%' ? 'Healthy' : 'Warning' },
        { service: 'Disk', status: health.disk_usage < '80%' ? 'Healthy' : 'Warning' },
      ]
    : [];

  const entityStatusData = entities
    ? Object.entries(
        entities.reduce((acc, entity) => {
          const status = (entity.status || entity.processing_status || 'unknown') as string;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      ).map(([status, count]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1),
        value: count,
      }))
    : [];

  if (statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: { xs: 1.5, md: 0 },
        mb: 4,
        p: 3,
        backgroundColor: 'background.paper',
        borderRadius: 3,
        color: 'text.primary',
        border: '1px solid rgba(28, 233, 224, 0.25)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            background: 'rgba(255, 255, 255, 0.2)', 
            width: 56, 
            height: 56,
            backdropFilter: 'blur(10px)',
          }}>
            <AnalyticsIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Dashboard
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Welcome back! Here's what's happening with your system.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={statsLoading}
          sx={{
            px: { xs: 1.5, md: 2.5 },
            py: { xs: 0.75, md: 1 },
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            minWidth: { xs: 104, md: 'auto' },
            alignSelf: { xs: 'flex-start', md: 'center' },
            whiteSpace: 'nowrap'
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Users
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.total_users || users.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    +12% from last month
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  background: 'rgba(255, 255, 255, 0.08)', 
                  width: 56, 
                  height: 56,
                  backdropFilter: 'blur(10px)',
                }}>
                  <PeopleIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Entities
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stats?.total_entities || entities.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    +8% from last month
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  background: 'rgba(255, 255, 255, 0.08)', 
                  width: 56, 
                  height: 56,
                  backdropFilter: 'blur(10px)',
                }}>
                  <StorageIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Active Files
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {entities.filter(e => e.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    +15% from last month
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  background: 'rgba(255, 255, 255, 0.08)', 
                  width: 56, 
                  height: 56,
                  backdropFilter: 'blur(10px)',
                }}>
                  <FileIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                    Growth Rate
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    +23%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Excellent progress!
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  background: 'rgba(255, 255, 255, 0.08)', 
                  width: 56, 
                  height: 56,
                  color: 'inherit',
                }}>
                  <TrendingUpIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    System Health
                  </Typography>
                  <Typography variant="h6" component="h2">
                    {health?.uptime || 'N/A'}
                  </Typography>
                </Box>
                <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Box mt={2}>
                <Chip
                  label={health?.status || 'Unknown'}
                  color={health?.status === 'healthy' ? 'success' : 'warning'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Processing Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Data Processing
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProcessData}
              disabled={isProcessing}
              startIcon={<RefreshIcon />}
            >
              {isProcessing ? 'Processing...' : 'Open Data Processing'}
            </Button>
          </Box>

          {isProcessing && status && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Processing Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {status.progress}% Complete
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={status.progress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {status.processed_entities} / {status.total_entities} entities processed
                </Typography>
                {status.estimated_time_remaining && (
                  <Typography variant="caption" color="text.secondary">
                    ~{Math.ceil(status.estimated_time_remaining / 60)} min remaining
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {history.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Recent Processing History
              </Typography>
              <Grid container spacing={2}>
                {history.slice(0, 3).map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(item.started_at).toLocaleDateString()}
                        </Typography>
                        <Chip
                          label={item.status}
                          color={
                            item.status === 'completed' ? 'success' :
                            item.status === 'failed' ? 'error' : 'warning'
                          }
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {item.processed_entities} entities, {item.processed_files} files
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.processing_time}s • {item.triggered_by}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* User Role Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Role Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={roleDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Entity Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Knowledge Base Status
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={entityStatusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Health */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Health Overview
          </Typography>
          <Grid container spacing={2}>
            {systemHealthData.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    border: '1px solid rgba(28, 233, 224, 0.25)',
                    borderRadius: 2,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 28px rgba(28, 233, 224, 0.15)'
                    }
                  }}
                >
                  <Box display="flex" justifyContent="center" mb={1}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: (t) => `linear-gradient(118deg, ${t.palette.secondary.main}55, ${t.palette.primary.main})`,
                        boxShadow: (t) => `0 6px 18px ${t.palette.primary.main}55`,
                      }}
                    >
                      {item.status === 'Healthy' ? (
                        <CheckCircleIcon sx={{ color: 'common.white' }} />
                      ) : item.status === 'Warning' ? (
                        <WarningIcon sx={{ color: 'common.white' }} />
                      ) : (
                        <ErrorIcon sx={{ color: 'common.white' }} />
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {item.service}
                  </Typography>
                  <Chip
                    label={item.status}
                    color={item.status === 'Healthy' ? 'success' : 'warning'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Knowledge Bases
          </Typography>
          <Grid container spacing={2}>
            {entities?.slice(0, 4).map((entity) => (
              <Grid item xs={12} sm={6} md={3} key={entity.id}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {entity.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {entity.description?.substring(0, 50)}...
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={entity.status}
                      color={
                        entity.status === 'active' ? 'success' :
                        entity.status === 'processing' ? 'warning' : 'default'
                      }
                      size="small"
                    />
                    <Chip
                      label={`${entity.file_count} files`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
